import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useNavItems, isNavItemActive } from "@/components/Sidebar";
import { cn } from "@/lib/utils";

// Most-used destinations get a tab; everything else lives under "More"
const PRIMARY = ["Dashboard", "Patients", "Appointments", "Billing"];

/**
 * Phone-only bottom tab bar (below the sm breakpoint). Tablets and desktops keep the sidebar.
 * Its height is --mobile-nav-height (index.css), which sticky bottom bars and page padding use.
 */
export default function MobileNav() {
    const location = useLocation();
    const items = useNavItems();
    const [moreOpen, setMoreOpen] = useState(false);

    const primary = PRIMARY.map(l => items.find(i => i.label === l)).filter((i): i is NonNullable<typeof i> => !!i);
    const rest = items.filter(i => !primary.includes(i));
    const moreActive = rest.some(i => isNavItemActive(location.pathname, i.to, i.exactMatch));
    const columns = primary.length + (rest.length ? 1 : 0);

    const tabClass = (active: boolean) => cn(
        "relative flex flex-col items-center justify-center gap-0.5 h-14 w-full text-2xs font-medium transition-colors",
        active ? "text-primary" : "text-muted-foreground active:text-foreground"
    );
    const indicator = <span aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-primary" />;

    return (
        <>
            <nav aria-label="Main navigation"
                className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 pb-[env(safe-area-inset-bottom)]">
                <ul className="grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                    {primary.map(({ label, icon: Icon, to, exactMatch }) => {
                        const active = isNavItemActive(location.pathname, to, exactMatch);
                        return (
                            <li key={to}>
                                <NavLink to={to} className={tabClass(active)} aria-current={active ? "page" : undefined}>
                                    {active && indicator}
                                    <Icon className="w-5 h-5" />
                                    {label}
                                </NavLink>
                            </li>
                        );
                    })}
                    {rest.length > 0 && (
                        <li>
                            <button type="button" onClick={() => setMoreOpen(true)} className={tabClass(moreActive)} aria-haspopup="dialog">
                                {moreActive && indicator}
                                <LayoutGrid className="w-5 h-5" />
                                More
                            </button>
                        </li>
                    )}
                </ul>
            </nav>

            <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                <SheetContent side="bottom" className="sm:hidden rounded-t-2xl px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                    <SheetHeader className="mb-3 text-left">
                        <SheetTitle className="text-sm">More</SheetTitle>
                    </SheetHeader>
                    <ul className="grid grid-cols-3 gap-2">
                        {rest.map(({ label, icon: Icon, to, exactMatch }) => {
                            const active = isNavItemActive(location.pathname, to, exactMatch);
                            return (
                                <li key={to}>
                                    <NavLink to={to} onClick={() => setMoreOpen(false)} aria-current={active ? "page" : undefined}
                                        className={cn(
                                            "flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium text-center transition-colors",
                                            active ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground active:bg-muted"
                                        )}>
                                        <Icon className="w-5 h-5" />
                                        {label}
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>
                </SheetContent>
            </Sheet>
        </>
    );
}
